// Port of ios/Sources/ViewModels/ClosedConversationsPresentation.swift.
//
// The conversations a reader closed (shared/API.md §29): still real, still active, just not in
// the sidebar — and the row a "start a conversation" sheet offers to bring one straight back.
import { displayName, swiftStringLess } from './channelListDerivation';
import { preferenceFor, type Preference } from './directChannelVisibility';
import type { ChannelRecord, UserRecord } from './records';

export type Detail = { kind: 'direct'; username: string | null } | { kind: 'group'; participantCount: number };

export interface Row {
  channelId: string;
  channelType: string;
  name: string;
  detail: Detail;
  preference: Preference;
  avatarUserId: string | null;
  avatarLastPictureUpdate: number;
  participantCount: number;
  lastPostAt: number;
}

/**
 * SyncMapping.dmOtherParticipantId (the Swift file calls it; core cannot import the worker's
 * mapping): the other half of "<a>__<b>", null on the self-DM or an unparseable name.
 */
function dmOtherParticipantId(channelName: string, currentUserId: string): string | null {
  const parts = channelName.split('__');
  if (parts.length !== 2) return null;
  const other = parts[0] === currentUserId ? parts[1] : parts[0];
  return other === currentUserId ? null : other;
}

/**
 * Active D/G conversations NOT in the sidebar, most recently used first. A conversation whose
 * preference row cannot be named is dropped — the action could not be offered.
 */
export function rows(args: {
  conversations: readonly ChannelRecord[];
  visibleChannelIds: ReadonlySet<string>;
  currentUserId: string;
  usersById: ReadonlyMap<string, UserRecord>;
  participantsByChannelId: ReadonlyMap<string, readonly string[]>;
  unknownUserLabel: string;
  unknownGroupLabel: string;
}): Row[] {
  const { currentUserId, usersById } = args;
  const out: Row[] = [];
  for (const channel of args.conversations) {
    if (channel.type !== 'D' && channel.type !== 'G') continue;
    if (channel.deleteAt !== 0 || args.visibleChannelIds.has(channel.id)) continue;
    const otherUserId = channel.type === 'D' ? dmOtherParticipantId(channel.name, currentUserId) : null;
    const preference = preferenceFor(channel.type, channel.id, otherUserId);
    if (!preference) continue;
    const participantIds = args.participantsByChannelId.get(channel.id) ?? [];
    const participantCount = channel.type === 'G' ? participantIds.filter((id) => id !== currentUserId).length : 0;
    const otherUser = otherUserId ? usersById.get(otherUserId) ?? null : null;
    out.push({
      channelId: channel.id,
      channelType: channel.type,
      name: displayName(channel, {
        currentUserId,
        usersById,
        participantIds,
        unknownUserFallback: channel.type === 'G' ? args.unknownGroupLabel : args.unknownUserLabel,
      }),
      detail: channel.type === 'D' ? { kind: 'direct', username: otherUser?.username ?? null } : { kind: 'group', participantCount },
      preference,
      avatarUserId: otherUser?.id ?? null,
      avatarLastPictureUpdate: otherUser?.lastPictureUpdate ?? 0,
      participantCount,
      lastPostAt: channel.lastPostAt,
    });
  }
  return out.sort((a, b) => {
    if (a.lastPostAt !== b.lastPostAt) return b.lastPostAt - a.lastPostAt;
    const na = a.name.toLowerCase();
    const nb = b.name.toLowerCase();
    if (na !== nb) return swiftStringLess(na, nb) ? -1 : 1;
    return a.channelId < b.channelId ? -1 : a.channelId > b.channelId ? 1 : 0;
  });
}

/** `range(of:options: [.caseInsensitive, .diacriticInsensitive])`: NFD minus marks, lowercased. */
function foldBoth(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

export function filter(list: readonly Row[], query: string): Row[] {
  const normalized = query.trim();
  if (normalized === '') return [...list];
  const needle = foldBoth(normalized);
  const lowered = normalized.toLowerCase();
  return list.filter((row) => {
    if (foldBoth(row.name).includes(needle)) return true;
    // `.caseInsensitive` alone for the handle: usernames are ASCII by server rule.
    if (row.detail.kind === 'direct' && row.detail.username && row.detail.username.toLowerCase().includes(lowered)) return true;
    return false;
  });
}

export const ClosedConversationsPresentation = { rows, filter } as const;
